<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Tooltip
    :disabled="!hasTooltip || focused"
    :text="getTooltipText"
    :ui="{
      ...$props.ui?.hint,
      anchor: ['epk-input__tooltip__anchor', $props.ui?.hint?.anchor],
    }"
  >
    <Input
      ref="inputRef"
      v-bind="$props"
      :ui="{
        ...$props.ui,
        label: [
          'epk-input__label',
          { 'epk-input__label_readonly': readonly },
          { 'epk-input__label_disabled': disabled },
          $props.ui?.label,
        ],
        input: [
          'epk-input__input',
          { 'epk-input__input_readonly': readonly },
          { 'epk-input__input_disabled': disabled },
          $props.ui?.input,
        ],
      }"
      :disabled="readonly ? false : disabled"
      :model-value="value"
      @update:model-value="onInput"
      @on-esc="handleEscKeydown"
      @on-enter="handleEnterKeydown"
      @focus="handleFocus"
      @blur="handleBlur"
    >
      <template v-if="$slots.top || required" #top>
        <slot name="top">
          <div v-if="required" class="mks-flex mks-items-start mks-gap-4">
            <div>{{ label }}</div>
            <Icon value="snowflake" :style="'font-size: 12px;'" />
          </div>
        </slot>
      </template>
      <template v-if="$slots.before" #before><slot name="before" /></template>
      <template v-if="$slots.after || hasIconClose" #after>
        <slot name="after">
          <Icon
            v-if="hasIconClose && value"
            value="tiny-close"
            :ui="{ icon: 'epk-input__input__after_close' }"
            @click.stop="onInput('')"
          />
        </slot>
      </template>
    </Input>
  </Tooltip>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { replaceSymbols } from '~packages/epk-front-shared';
import { Icon } from '~packages/epk-front-shared/ui';

import { Tooltip } from '@/shared/ui';
import { Input, type InputProps } from '@mkskom/ui';
import { useFocus } from '@vueuse/core';

import type { IInputUi } from './types';

export interface IInputProps extends /* @vue-ignore */ InputProps {
  label?: InputProps['label'];
  value?: InputProps['value'];
  position?: InputProps['position'];
  size?: InputProps['size'];
  error?: InputProps['error'];
  errorText?: InputProps['errorText'];
  readonly?: InputProps['readonly'];
  disabled?: InputProps['disabled'];
  hasBorder?: InputProps['hasBorder'];
  required?: InputProps['required'];
  ui?: IInputUi;
  placeholder?: InputProps['placeholder'];
  needDelEndSymbols?: boolean;
  hasTooltip?: boolean;
  hasIconClose?: boolean;
}

interface InputInstance {
  input: HTMLInputElement | null;
}

const props = withDefaults(defineProps<IInputProps>(), {
  label: '',
  position: 'top',
  value: '',
  size: 'm',
  error: false,
  errorText: '',
  hasBorder: true,
  readonly: false,
  disabled: false,
  required: false,
  placeholder: 'Введите значение',
  needDelEndSymbols: false,
  hasTooltip: true,
  hasIconClose: false,
});

const emit = defineEmits<{
  (e: 'input', v?: InputProps['modelValue']): void;
  (e: 'onEsc', v: KeyboardEvent): void;
  (e: 'onEnter', v: KeyboardEvent): void;
  (e: 'focus', v: Event): void;
  (e: 'focusout', v: Event): void;
}>();

const instance = getCurrentInstance();

// Референс на компонент Input
const inputRef = ref<InstanceType<typeof Input> | null>(null);

// Референс на внутренний DOM-элемент input
const innerInputRef = ref<HTMLInputElement | null>(null);

const { focused } = useFocus(innerInputRef);

const getTooltipText = computed(() => (focused.value ? '' : props.value));

const handleEscKeydown = (e: KeyboardEvent) => emit('onEsc', e);
const handleEnterKeydown = (e: KeyboardEvent) => emit('onEnter', e);
const handleFocus = (e: Event) => emit('focus', e);
const handleBlur = (e: Event) => {
  let v = (e.target as HTMLInputElement).value.trimEnd();

  if (props.needDelEndSymbols)
    v = replaceSymbols(v, '([^a-zA-Zа-яёА-ЯЁ0-9]+$)', '');

  emit('input', v);
  emit('focusout', e);
};
const onInput = (v: InputProps['modelValue']) => {
  let inputValue = v?.toString() || '';
  if (inputValue.startsWith(' ')) inputValue = inputValue.trim();
  emit('input', normalizeValue(inputValue));

  instance?.proxy?.$forceUpdate();
};

const normalizeValue = (v: string) =>
  replaceSymbols(v, '([^a-zA-Zа-яёА-ЯЁ0-9])\\1+', '$1');

onMounted(() => {
  if (inputRef.value) {
    innerInputRef.value = inputRef.value.input;
  }
});
</script>

<style lang="scss">
@import './Input';
</style>
